<template>
  <div>
    <v-dialog :value="visible" @input="$emit('close')" max-width="1200" persistent>
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>Turnos del Presupuesto - {{ nombrePaciente }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="$emit('close')">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-4">
          <!-- Sección Superior: Listado de turnos asociados -->
          <v-chip label color="transparent" text-color="black">
            <h3>Turnos Asociados</h3>
          </v-chip>

          <v-data-table
            v-if="turnosAsociados.length"
            :headers="headersAsociados"
            :items="turnosAsociados"
            class="elevation-1 mt-2"
            locale="es-ar"
            item-key="idTurnoPresupuesto"
            :items-per-page="5"
            :disable-sort="true"
            :footer-props="{ itemsPerPageText: 'Filas por página' }"
          >
            <template v-slot:item.fechaTurno="{ item }">
              {{ formatFecha(item.fechaTurno) }}
            </template>
            <template v-slot:item.hora="{ item }">
              {{ formatHora(item.hora) }}
            </template>
            <template v-slot:item.estadoTurno="{ item }">
              {{ formatEstado(item.estadoTurno) }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    medium
                    color="red"
                    v-on="on"
                    v-bind="attrs"
                    @click="desvincularTurno(item)"
                  >mdi-link-off</v-icon>
                </template>
                <span>Desvincular Turno</span>
              </v-tooltip>
            </template>
          </v-data-table>

          <v-alert v-else class="mt-2" color="purple" dark outlined>
            No hay turnos asignados aún
          </v-alert>

          <v-divider class="my-4"></v-divider>

          <!-- Sección Inferior: Asignar Turno (estilo circuitos) -->
          <v-chip label color="transparent" text-color="black">
            <h3>Asignar Nuevo Turno</h3>
          </v-chip>

          <!-- Selector de profesional -->
          <v-row dense class="mt-2">
            <v-col cols="12" md="5">
              <v-autocomplete
                v-model="profSeleccionado"
                :items="profesionales"
                item-text="nombreProfesional"
                item-value="idProfesional"
                label="Profesional"
                prepend-icon="mdi-account"
                dense
                outlined
                hide-details
                no-data-text="No hay profesionales configurados"
                @change="onProfesionalChange"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="3" class="d-flex align-center">
              <v-btn
                small
                outlined
                color="purple"
                :disabled="!profSeleccionado"
                @click="buscarTurnosExistentes"
              >
                <v-icon left small>mdi-link-plus</v-icon>
                Asociar turno existente
              </v-btn>
            </v-col>
          </v-row>

          <!-- Grilla de slots semanal -->
          <div v-if="profSeleccionado && slotsLoaded" class="mt-3">
            <!-- Navegación de semana -->
            <div class="d-flex align-center mb-2">
              <v-btn icon small @click="semanaAnterior" :disabled="cargandoSlots || esSemanaActual">
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <span class="text-caption font-weight-medium mx-2">
                {{ rangoSemanaStr }}
              </span>
              <v-btn icon small @click="semanaSiguiente" :disabled="cargandoSlots">
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
              <v-btn x-small outlined color="primary" @click="irAHoy" class="ml-2">Hoy</v-btn>
              <v-progress-circular
                v-if="cargandoSlots"
                indeterminate
                size="16"
                width="2"
                color="primary"
                class="ml-3"
              ></v-progress-circular>
            </div>

            <!-- Leyenda -->
            <div class="mb-2">
              <v-icon x-small color="green darken-1" class="mr-1">mdi-circle</v-icon>
              <span class="text-caption mr-3">Disponible (click = asignar)</span>
              <v-icon x-small color="red darken-1" class="mr-1">mdi-circle</v-icon>
              <span class="text-caption mr-3">Ocupado (click = entre turno)</span>
            </div>

            <!-- Días de la semana con slots -->
            <v-row dense>
              <v-col
                v-for="dia in diasSemana"
                :key="dia.diaIdx"
                cols="12"
                sm="6"
                md="4"
                lg="3"
              >
                <v-card
                  outlined
                  class="dia-card"
                  :class="{
                    'dia-pasado': dia.esPasado,
                    'dia-hoy': esHoy(dia.fecha)
                  }"
                >
                  <div class="dia-header pa-1 px-2">
                    <span class="text-caption font-weight-bold">{{ dia.nombreCorto }}</span>
                    <span class="text-caption ml-1 grey--text">{{ dia.fechaStr }}</span>
                  </div>
                  <v-divider></v-divider>
                  <div class="pa-1 chips-container" v-if="!dia.esPasado">
                    <v-chip
                      v-for="(slot, idx) in getSlotsDelDia(dia.diaIdx)"
                      :key="idx"
                      :color="getSlotColor(slot)"
                      :text-color="getSlotTextColor(slot)"
                      small
                      label
                      class="chip-slot mr-1 mb-1"
                      :disabled="!!slot.idExcepcion || asignando"
                      @click="clickSlot(slot)"
                    >
                      {{ slot.hora ? slot.hora.substring(0, 5) : '' }}
                    </v-chip>
                    <span
                      v-if="getSlotsDelDia(dia.diaIdx).length === 0"
                      class="text-caption grey--text pa-1"
                    >
                      Sin agenda
                    </span>
                  </div>
                  <div v-else class="pa-2 text-center">
                    <span class="text-caption grey--text">—</span>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </div>

          <!-- Loading slots -->
          <div v-if="profSeleccionado && cargandoSlots && !slotsLoaded" class="text-center pa-3">
            <v-progress-circular indeterminate size="24" color="primary"></v-progress-circular>
            <span class="text-caption ml-2">Cargando turnos disponibles...</span>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" @click="$emit('close')">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog para seleccionar Obra Social (y Tipo Turno si aplica) -->
    <v-dialog v-model="showObraSocialDialog" max-width="450" persistent>
      <v-card>
        <v-toolbar dark color="primary" dense>
          <v-toolbar-title class="text-subtitle-1">Confirmar Turno</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="cancelarObraSocial">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <p class="text-body-2 mb-3">
            Turno: <strong>{{ slotPendiente ? slotPendiente.fechaTurnoString + ' ' + (slotPendiente.hora || '').substring(0, 5) : '' }}</strong>
          </p>
          <v-autocomplete
            v-model="obraSocialSeleccionada"
            :items="obrasSociales"
            item-text="nombre"
            item-value="idObraSocial"
            label="Obra Social *"
            prepend-icon="mdi-shield-cross"
            dense
            outlined
            :loading="cargandoOS"
            no-data-text="No hay obras sociales configuradas"
          ></v-autocomplete>
          <v-select
            v-if="tiposTurno.length > 0"
            v-model="tipoTurnoSeleccionado"
            :items="tiposTurno"
            item-text="nombreTipoTurno"
            item-value="idTipoTurno"
            label="Tipo de Turno"
            prepend-icon="mdi-tag"
            dense
            outlined
            no-data-text="No hay tipos de turno"
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" text @click="cancelarObraSocial">Cancelar</v-btn>
          <v-btn color="success" :disabled="!obraSocialSeleccionada" :loading="asignando" @click="confirmarAsignacion">
            <v-icon left small>mdi-check</v-icon>
            Confirmar Turno
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog para asociar turno existente -->
    <v-dialog v-model="showAsociarDialog" max-width="600" persistent>
      <v-card>
        <v-toolbar dark color="purple" dense>
          <v-toolbar-title class="text-subtitle-1">Asociar Turno Existente</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="showAsociarDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-alert v-if="cargandoTurnosExistentes" type="info" outlined class="mb-2">
            Buscando turnos del paciente...
          </v-alert>
          <v-data-table
            v-if="turnosExistentes.length"
            :headers="headersExistentes"
            :items="turnosExistentes"
            class="elevation-1"
            locale="es-ar"
            item-key="idTurno"
            :items-per-page="5"
            :disable-sort="true"
            show-select
            single-select
            v-model="turnoExistenteSeleccionado"
            :footer-props="{ itemsPerPageText: 'Filas por página' }"
          >
            <template v-slot:item.hora="{ item }">
              {{ formatHora(item.hora) }}
            </template>
            <template v-slot:item.estadoTurno="{ item }">
              {{ formatEstado(item.estadoTurno) }}
            </template>
          </v-data-table>
          <v-alert v-else-if="!cargandoTurnosExistentes" type="info" outlined>
            No se encontraron turnos del paciente con este profesional que no estén ya asociados al presupuesto.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" text @click="showAsociarDialog = false">Cancelar</v-btn>
          <v-btn
            color="success"
            :disabled="!turnoExistenteSeleccionado.length"
            :loading="asociando"
            @click="asociarTurnoExistente"
          >
            <v-icon left small>mdi-link-plus</v-icon>
            Asociar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "../../axios-auth.js";

export default {
  props: {
    visible: { type: Boolean, default: false },
    idPresupuesto: { type: Number, default: 0 },
    idPaciente: { type: Number, default: 0 },
    nombrePaciente: { type: String, default: "" },
  },
  data() {
    return {
      // Turnos asociados
      turnosAsociados: [],
      headersAsociados: [
        { text: "Fecha", value: "fechaTurno" },
        { text: "Hora", value: "hora" },
        { text: "Profesional", value: "nombreProfesional" },
        { text: "Estado", value: "estadoTurno" },
        { text: "Acciones", value: "actions", sortable: false },
      ],
      // Asignar turno
      profSeleccionado: null,
      fechaInicioSemana: null,
      slotsData: [[], [], [], [], [], [], []],
      slotsLoaded: false,
      cargandoSlots: false,
      asignando: false,
      // Obra social dialog
      showObraSocialDialog: false,
      obrasSociales: [],
      obraSocialSeleccionada: null,
      cargandoOS: false,
      slotPendiente: null,
      // Tipos de turno
      tiposTurno: [],
      tipoTurnoSeleccionado: null,
      // Asociar turno existente
      showAsociarDialog: false,
      turnosExistentes: [],
      headersExistentes: [
        { text: "Fecha", value: "fechaTurnoString" },
        { text: "Hora", value: "hora" },
        { text: "Estado", value: "estadoTurno" },
      ],
      turnoExistenteSeleccionado: [],
      cargandoTurnosExistentes: false,
      asociando: false,
    };
  },
  computed: {
    profesionales() {
      const profs = this.$store.state.usuario.profesionales || [];
      return [...profs].sort((a, b) =>
        (a.nombreProfesional || "").localeCompare(b.nombreProfesional || "")
      );
    },
    diasSemana() {
      if (!this.fechaInicioSemana) return [];
      const dias = [];
      const nombresCortos = ["LUN", "MAR", "MIÉ", "JUE", "VIE", "SÁB", "DOM"];
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      for (let i = 0; i < 7; i++) {
        const d = new Date(this.fechaInicioSemana);
        d.setDate(d.getDate() + i);
        dias.push({
          nombreCorto: nombresCortos[i],
          fechaStr: d.getDate() + "/" + (d.getMonth() + 1),
          fecha: new Date(d),
          diaIdx: i,
          esPasado: d < hoy,
        });
      }
      return dias;
    },
    rangoSemanaStr() {
      if (!this.fechaInicioSemana) return "";
      const inicio = new Date(this.fechaInicioSemana);
      const fin = new Date(this.fechaInicioSemana);
      fin.setDate(fin.getDate() + 6);
      return (
        inicio.getDate() + "/" + (inicio.getMonth() + 1) +
        " - " +
        fin.getDate() + "/" + (fin.getMonth() + 1) + "/" + fin.getFullYear()
      );
    },
    esSemanaActual() {
      if (!this.fechaInicioSemana) return true;
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      const inicioSemanaHoy = this.calcularInicioSemana(hoy);
      return this.fechaInicioSemana.getTime() <= inicioSemanaHoy.getTime();
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.cargarTurnosPresupuesto();
        this.resetAsignacion();
      }
    },
  },
  methods: {
    // === Turnos asociados ===
    cargarTurnosPresupuesto() {
      let self = this;
      axios
        .get("/Caja/BuscarTurnosPresupuesto", {
          params: { idPresupuesto: self.idPresupuesto },
        })
        .then(function (response) {
          if (response.data != null && response.data != "") {
            self.turnosAsociados = response.data;
          } else {
            self.turnosAsociados = [];
          }
        })
        .catch(function () {
          self.turnosAsociados = [];
        });
    },

    desvincularTurno(item) {
      let self = this;
      this.$swal({
        title: "Desvincular Turno",
        text: "¿Desea desvincular este turno del presupuesto?",
        icon: "warning",
        background: "#ececec",
        showCancelButton: true,
        confirmButtonText: "Si",
        cancelButtonText: "No",
        showCloseButton: true,
      }).then((result) => {
        if (result.value) {
          axios
            .get("/Caja/DesvincularTurnoPresupuesto", {
              params: { idTurnoPresupuesto: item.idTurnoPresupuesto },
            })
            .then(() => {
              self.showAlert("success", "Turno desvinculado exitosamente");
              self.cargarTurnosPresupuesto();
            })
            .catch(() => {
              self.showAlert("error", "Error al desvincular el turno");
            });
        }
      });
    },

    // === Asignación de turnos ===
    resetAsignacion() {
      this.profSeleccionado = null;
      this.slotsData = [[], [], [], [], [], [], []];
      this.slotsLoaded = false;
      this.cargandoSlots = false;
      this.asignando = false;
      this.slotPendiente = null;
      this.tiposTurno = [];
      this.tipoTurnoSeleccionado = null;
    },

    onProfesionalChange() {
      if (!this.profSeleccionado) {
        this.slotsLoaded = false;
        this.slotsData = [[], [], [], [], [], [], []];
        return;
      }
      this.fechaInicioSemana = this.calcularInicioSemana(new Date());
      this.cargarSlots();
      this.cargarTiposTurno();
    },

    cargarTiposTurno() {
      let self = this;
      axios
        .get("/Profesional/" + this.profSeleccionado + "/tipos-turno")
        .then((response) => {
          self.tiposTurno = response.data || [];
          var defecto = self.tiposTurno.find((tt) => tt.porDefecto);
          self.tipoTurnoSeleccionado = defecto ? defecto.idTipoTurno : (self.tiposTurno.length > 0 ? self.tiposTurno[0].idTipoTurno : null);
        })
        .catch(() => {
          self.tiposTurno = [];
          self.tipoTurnoSeleccionado = null;
        });
    },

    cargarSlots() {
      if (!this.profSeleccionado || !this.fechaInicioSemana) return;

      this.cargandoSlots = true;
      const fecha = this.toISOLocal(this.fechaInicioSemana);
      const idInstitucion = this.$store.state.usuario.institucion.idInstitucion;

      let self = this;
      axios
        .get("/Turnos/ArmarTurnosWideOptimizado", {
          params: {
            idProfesional: self.profSeleccionado,
            fecha: fecha,
            idInstitucion: idInstitucion,
          },
        })
        .then((response) => {
          self.cargandoSlots = false;
          self.slotsLoaded = true;
          if (response.data && Array.isArray(response.data)) {
            self.slotsData = response.data;
          } else {
            self.slotsData = [[], [], [], [], [], [], []];
          }
        })
        .catch(() => {
          self.cargandoSlots = false;
          self.slotsLoaded = true;
          self.slotsData = [[], [], [], [], [], [], []];
        });
    },

    semanaAnterior() {
      const d = new Date(this.fechaInicioSemana);
      d.setDate(d.getDate() - 7);
      this.fechaInicioSemana = d;
      this.cargarSlots();
    },

    semanaSiguiente() {
      const d = new Date(this.fechaInicioSemana);
      d.setDate(d.getDate() + 7);
      this.fechaInicioSemana = d;
      this.cargarSlots();
    },

    irAHoy() {
      this.fechaInicioSemana = this.calcularInicioSemana(new Date());
      this.cargarSlots();
    },

    calcularInicioSemana(fecha) {
      const d = new Date(fecha);
      const day = d.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      d.setDate(d.getDate() + diff);
      d.setHours(0, 0, 0, 0);
      return d;
    },

    getSlotsDelDia(diaIdx) {
      if (!this.slotsData || !this.slotsData[diaIdx]) return [];
      return this.slotsData[diaIdx];
    },

    esSlotDisponible(slot) {
      return slot.paciente === "Disponible" && !slot.idTurno;
    },

    getSlotColor(slot) {
      if (slot.idExcepcion) return "grey lighten-2";
      if (!slot.idTurno && slot.paciente === "Disponible") return "green lighten-4";
      return "red lighten-4";
    },

    getSlotTextColor(slot) {
      if (slot.idExcepcion) return "grey darken-1";
      if (!slot.idTurno && slot.paciente === "Disponible") return "green darken-3";
      return "red darken-3";
    },

    clickSlot(slot) {
      if (slot.idExcepcion) return;
      if (this.asignando) return;

      if (this.esSlotDisponible(slot)) {
        this.iniciarAsignacion(slot);
      } else {
        // Entre turno: restar 1 minuto a la hora
        const hora = slot.hora ? slot.hora.substring(0, 5) : "";
        const parts = hora.split(":");
        let h = parseInt(parts[0]);
        let m = parseInt(parts[1]) - 1;
        if (m < 0) { m = 59; h--; }
        const horaET = String(h).padStart(2, "0") + ":" + String(m).padStart(2, "0");

        const slotET = {
          ...slot,
          hora: horaET + ":00",
          entreTurno: 1,
        };
        this.iniciarAsignacion(slotET);
      }
    },

    iniciarAsignacion(slot) {
      this.slotPendiente = slot;
      this.obraSocialSeleccionada = null;

      if (this.obrasSociales.length === 0) {
        this.cargarObrasSociales();
      }

      this.showObraSocialDialog = true;
    },

    cargarObrasSociales() {
      this.cargandoOS = true;
      const idInstitucion = this.$store.state.usuario.institucion.idInstitucion;
      let self = this;
      axios
        .get("/ObraSocial/GetObraSocialActivasDeInst", {
          params: { idInstitucion: idInstitucion, idProfesional: self.profSeleccionado || 0 },
        })
        .then((response) => {
          self.cargandoOS = false;
          self.obrasSociales = response.data || [];
          if (self.obrasSociales.length === 1) {
            self.obraSocialSeleccionada = self.obrasSociales[0].idObraSocial;
          }
        })
        .catch(() => {
          self.cargandoOS = false;
          self.obrasSociales = [];
        });
    },

    cancelarObraSocial() {
      this.showObraSocialDialog = false;
      this.slotPendiente = null;
      this.obraSocialSeleccionada = null;
    },

    confirmarAsignacion() {
      if (!this.obraSocialSeleccionada) return;
      if (!this.slotPendiente) return;

      this.asignando = true;

      const slot = this.slotPendiente;
      const fechaTurnoString = slot.fechaTurnoString;
      const horaInicioString = slot.hora ? slot.hora.substring(0, 5) : "";
      const duracionTurno = slot.tiempoTurno || 15;
      const entreTurno = slot.entreTurno || 0;

      let payload = {
        idPresupuesto: this.idPresupuesto,
        idPaciente: this.idPaciente,
        idProfesional: this.profSeleccionado,
        fechaTurnoString: fechaTurnoString,
        horaInicioString: horaInicioString,
        duracionTurno: duracionTurno,
        entreTurno: entreTurno,
        idObraSocial: this.obraSocialSeleccionada,
        idTipoTurno: this.tipoTurnoSeleccionado || 0,
      };

      let self = this;
      axios
        .post("/Caja/AsignarTurnoPresupuestoSlot", payload)
        .then(() => {
          self.asignando = false;
          self.showObraSocialDialog = false;
          self.slotPendiente = null;
          self.showAlert("success", "Turno asignado correctamente");
          self.cargarTurnosPresupuesto();
          self.cargarSlots(); // recargar para ver el slot ahora ocupado
        })
        .catch((err) => {
          self.asignando = false;
          const msg = err.response && err.response.data
            ? err.response.data
            : "Error al asignar el turno";
          self.showAlert("error", msg);
        });
    },

    // === Asociar turno existente ===
    buscarTurnosExistentes() {
      if (!this.profSeleccionado) return;
      this.showAsociarDialog = true;
      this.cargandoTurnosExistentes = true;
      this.turnosExistentes = [];
      this.turnoExistenteSeleccionado = [];

      let self = this;
      axios
        .get("/Caja/BuscarTurnosPacienteProfesional", {
          params: {
            idPaciente: self.idPaciente,
            idProfesional: self.profSeleccionado,
            idPresupuesto: self.idPresupuesto,
          },
        })
        .then((response) => {
          self.cargandoTurnosExistentes = false;
          if (response.data != null && response.data != "") {
            self.turnosExistentes = response.data;
          } else {
            self.turnosExistentes = [];
          }
        })
        .catch(() => {
          self.cargandoTurnosExistentes = false;
          self.turnosExistentes = [];
        });
    },

    asociarTurnoExistente() {
      if (!this.turnoExistenteSeleccionado.length) return;
      this.asociando = true;

      const idTurno = this.turnoExistenteSeleccionado[0].idTurno;
      let self = this;
      axios
        .post("/Caja/AsignarTurnoPresupuesto", {
          idPresupuesto: String(self.idPresupuesto),
          idTurno: String(idTurno),
        })
        .then((response) => {
          self.asociando = false;
          if (response.data && response.data.resultado === 1) {
            self.showAsociarDialog = false;
            self.showAlert("success", "Turno asociado correctamente");
            self.cargarTurnosPresupuesto();
          } else {
            self.showAlert("error", response.data ? response.data.mensaje : "Error al asociar");
          }
        })
        .catch(() => {
          self.asociando = false;
          self.showAlert("error", "Error al asociar el turno");
        });
    },

    // === Utilidades ===
    esHoy(fecha) {
      const hoy = new Date();
      return (
        fecha.getDate() === hoy.getDate() &&
        fecha.getMonth() === hoy.getMonth() &&
        fecha.getFullYear() === hoy.getFullYear()
      );
    },

    formatHora(hora) {
      if (hora == null) return "";
      const h = Math.floor(hora / 100);
      const m = hora % 100;
      return h.toString().padStart(2, "0") + ":" + m.toString().padStart(2, "0");
    },

    formatFecha(fecha) {
      if (!fecha) return "(eliminado)";
      const d = new Date(fecha);
      return d.getDate().toString().padStart(2, "0") + "/" +
        (d.getMonth() + 1).toString().padStart(2, "0") + "/" +
        d.getFullYear();
    },

    formatEstado(estado) {
      if (estado == null) return "(eliminado)";
      switch (estado) {
        case 1: return "Reservado";
        case 2: return "En espera";
        case 3: return "Atendido";
        case 4: return "Ausente";
        default: return estado.toString();
      }
    },

    toISOLocal(d) {
      const z = (n) => ("0" + n).slice(-2);
      return d.getFullYear() + "-" + z(d.getMonth() + 1) + "-" + z(d.getDate());
    },

    showAlert(icon, title) {
      this.$swal.fire({
        icon: icon,
        title: title,
        showConfirmButton: false,
        background: "#ececec",
        timer: 2000,
      });
    },
  },
};
</script>

<style scoped>
.dia-card {
  transition: all 0.2s;
  min-height: 60px;
}

.dia-pasado {
  opacity: 0.35;
}

.dia-hoy {
  border: 2px solid #1976d2 !important;
}

.dia-header {
  background: #fafafa;
}

.chips-container {
  display: flex;
  flex-wrap: wrap;
  min-height: 32px;
}

.chip-slot {
  cursor: pointer;
  font-weight: 600;
  font-size: 0.7rem !important;
  min-width: 46px;
  justify-content: center;
}

.chip-slot:hover:not(.v-chip--disabled) {
  transform: scale(1.05);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}
</style>
